export type MessageRenderSegment =
  | { type: 'markdown'; content: string }
  | { type: 'block-math'; content: string }
  | { type: 'inline-math'; content: string };

type DelimiterMatch = {
  type: 'block-math' | 'inline-math';
  start: number;
  end: number;
  latex: string;
};

function tryParseBracketLatex(text: string, openBracket: number): DelimiterMatch | null {
  let i = openBracket + 1;
  while (i < text.length && (text[i] === ' ' || text[i] === '\n' || text[i] === '\t')) i += 1;
  if (text[i] !== '\\') return null;

  const latexStart = i;
  let braceDepth = 0;

  while (i < text.length) {
    const ch = text[i];
    if (ch === '\\') {
      i += 1;
      if (i < text.length) i += 1;
      continue;
    }
    if (ch === '{') braceDepth += 1;
    else if (ch === '}') braceDepth = Math.max(0, braceDepth - 1);
    else if (ch === ']' && braceDepth === 0) {
      return {
        type: 'block-math',
        start: openBracket,
        end: i + 1,
        latex: text.slice(latexStart, i).trim(),
      };
    }
    i += 1;
  }

  return null;
}

function findNextMathDelimiter(text: string, from: number): DelimiterMatch | null {
  let earliest: DelimiterMatch | null = null;

  const consider = (match: DelimiterMatch | null) => {
    if (match && (!earliest || match.start < earliest.start)) {
      earliest = match;
    }
  };

  for (let idx = text.indexOf('$$', from); idx !== -1; idx = text.indexOf('$$', idx + 2)) {
    const end = text.indexOf('$$', idx + 2);
    if (end === -1) break;
    consider({
      type: 'block-math',
      start: idx,
      end: end + 2,
      latex: text.slice(idx + 2, end).trim(),
    });
    break;
  }

  for (let idx = text.indexOf('\\[', from); idx !== -1; idx = text.indexOf('\\[', idx + 2)) {
    const end = text.indexOf('\\]', idx + 2);
    if (end === -1) break;
    consider({
      type: 'block-math',
      start: idx,
      end: end + 2,
      latex: text.slice(idx + 2, end).trim(),
    });
    break;
  }

  for (let idx = text.indexOf('\\(', from); idx !== -1; idx = text.indexOf('\\(', idx + 2)) {
    const end = text.indexOf('\\)', idx + 2);
    if (end === -1) break;
    consider({
      type: 'inline-math',
      start: idx,
      end: end + 2,
      latex: text.slice(idx + 2, end).trim(),
    });
    break;
  }

  for (let idx = from; idx < text.length; idx += 1) {
    if (text[idx] !== '$' || text[idx + 1] === '$') continue;

    let end = idx + 1;
    while (end < text.length) {
      if (text[end] === '$' && text[end - 1] !== '\\') {
        consider({
          type: 'inline-math',
          start: idx,
          end: end + 1,
          latex: text.slice(idx + 1, end).trim(),
        });
        break;
      }
      end += 1;
    }
    break;
  }

  for (let idx = text.indexOf('[', from); idx !== -1; idx = text.indexOf('[', idx + 1)) {
    consider(tryParseBracketLatex(text, idx));
  }

  return earliest;
}

function stripIncompleteLatex(text: string): string {
  let dollarSearchFrom = 0;
  let open = text.indexOf('$$', dollarSearchFrom);
  while (open !== -1) {
    const close = text.indexOf('$$', open + 2);
    if (close === -1) return text.slice(0, open);
    dollarSearchFrom = close + 2;
    open = text.indexOf('$$', dollarSearchFrom);
  }

  const displayIdx = text.lastIndexOf('\\[');
  if (displayIdx !== -1) {
    const closingIdx = text.indexOf('\\]', displayIdx + 2);
    if (closingIdx === -1) return text.slice(0, displayIdx);
  }

  const inlineIdx = text.lastIndexOf('\\(');
  if (inlineIdx !== -1) {
    const closingIdx = text.indexOf('\\)', inlineIdx + 2);
    if (closingIdx === -1) return text.slice(0, inlineIdx);
  }

  const lastOpenBracket = text.lastIndexOf('[');
  if (lastOpenBracket !== -1) {
    const tail = text.slice(lastOpenBracket);
    if (tryParseBracketLatex(text, lastOpenBracket) === null) {
      let i = 1;
      while (i < tail.length && (tail[i] === ' ' || tail[i] === '\n' || tail[i] === '\t')) i += 1;
      if (tail[i] === '\\') return text.slice(0, lastOpenBracket);
    }
  }

  return text;
}

export function splitMessageRenderSegments(text: string): MessageRenderSegment[] {
  const safeText = stripIncompleteLatex(text);
  const segments: MessageRenderSegment[] = [];
  let cursor = 0;

  while (cursor < safeText.length) {
    const match = findNextMathDelimiter(safeText, cursor);

    if (!match) {
      const remainder = safeText.slice(cursor);
      if (remainder) segments.push({ type: 'markdown', content: remainder });
      break;
    }

    const before = safeText.slice(cursor, match.start);
    if (before) segments.push({ type: 'markdown', content: before });

    if (match.latex) {
      segments.push({ type: match.type, content: match.latex });
    }

    cursor = match.end;
  }

  return segments;
}
